import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Clock, Sparkles, Trophy, ArrowRight, AlertCircle, CheckCircle2 } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import Seo from '@/components/shared/Seo';
import { QuestionPassage, QuestionTableData, QuestionStem, AnswerOptionList } from '@/components/exam/QuestionDisplay';
import { gmatApi, goToGoogleAuth } from '@/lib/gmatApi';
import { toast } from 'sonner';

const SECTIONS_ORDER = ['quant', 'verbal', 'data_insights'];
const SECTION_LABEL = { quant: 'Quantitative', verbal: 'Verbal', data_insights: 'Data Insights' };

const GmatMockTestPage = () => {
  const [needsLogin, setNeedsLogin] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | starting | running | finalizing | done
  const [mock, setMock] = useState(null);
  const [secIdx, setSecIdx] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [question, setQuestion] = useState(null);
  const [selected, setSelected] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [results, setResults] = useState(null);
  const [answeredMap, setAnsweredMap] = useState({}); // { qid: 'a' }
  const [availability, setAvailability] = useState(null); // { counts, blueprint }
  const [authChecked, setAuthChecked] = useState(false);
  const [mobile, setMobile] = useState(false);
  const tickRef = useRef(null);

  useEffect(() => {
    gmatApi.authMe()
      .then(() => gmatApi.mockAvailability().then(setAvailability).catch(() => {}))
      .catch((e) => { if (e.status === 401) setNeedsLogin(true); })
      .finally(() => setAuthChecked(true));
  }, []);

  const currentSection = mock?.sections?.[secIdx] || SECTIONS_ORDER[secIdx];
  const sectionPlan = mock?.plan?.[currentSection] || [];
  const currentQid = sectionPlan[qIdx];

  // Load question whenever section/q changes
  useEffect(() => {
    if (phase !== 'running' || !mock?.mock_session_id || !currentQid) return;
    setSelected(null);
    gmatApi.mockQuestion(mock.mock_session_id, currentQid)
      .then((r) => setQuestion(r.question))
      .catch((e) => toast.error(e.message));
  }, [phase, mock, currentQid]);

  // Timer
  useEffect(() => {
    if (phase !== 'running') return;
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(tickRef.current);
          advanceSection();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, secIdx]);

  const start = async () => {
    setPhase('starting');
    try {
      const m = await gmatApi.mockStart({ sections: SECTIONS_ORDER });
      // Defensive: ensure plan + sections always exist before flipping into running phase
      const plan = m?.plan || {};
      const blueprint = m?.blueprint || {};
      const sections = (m?.sections && m.sections.length ? m.sections : SECTIONS_ORDER).filter(
        (s) => Array.isArray(plan[s]) && plan[s].length > 0,
      );
      if (!sections.length) {
        toast.error('No questions available for this mock test yet. Please contact admin.');
        setPhase('idle');
        return;
      }
      setMock({ ...m, sections, plan, blueprint });
      setSecIdx(0);
      setQIdx(0);
      setAnsweredMap({});
      setSecondsLeft((blueprint[sections[0]]?.duration_min || 45) * 60);
      setPhase('running');
    } catch (e) {
      if (e.status === 401) setNeedsLogin(true);
      else toast.error(e.message || 'Could not start mock');
      setPhase('idle');
    }
  };

  const submitAnswer = async () => {
    if (!selected || !question || !mock?.mock_session_id) return;
    setAnsweredMap((m) => ({ ...m, [question.id]: selected }));
    try {
      await gmatApi.practiceSubmit({
        question_id: question.id, selected_option: selected, seconds_spent: 0,
        is_mock: true, mock_session_id: mock.mock_session_id,
      });
    } catch (e) { /* swallow — mock continues */ }
    nextQuestion();
  };

  const nextQuestion = () => {
    if (qIdx + 1 < sectionPlan.length) setQIdx(qIdx + 1);
    else advanceSection();
  };

  const advanceSection = async () => {
    const sections = mock?.sections || [];
    if (secIdx + 1 < sections.length) {
      const next = sections[secIdx + 1];
      setSecIdx(secIdx + 1);
      setQIdx(0);
      setSecondsLeft((mock?.blueprint?.[next]?.duration_min || 45) * 60);
    } else if (mock?.mock_session_id) {
      setPhase('finalizing');
      try {
        const r = await gmatApi.mockFinalize(mock.mock_session_id);
        setResults(r);
        setPhase('done');
      } catch (e) {
        toast.error(e.message || 'Could not finalize');
        setPhase('idle');
      }
    } else {
      // Defensive: no mock session — bounce to idle rather than crash
      setPhase('idle');
    }
  };

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const sectionDurationSec = mock?.blueprint?.[currentSection]?.duration_min ? mock.blueprint[currentSection].duration_min * 60 : 1;
  const timePct = phase === 'running' ? Math.max(0, (secondsLeft / sectionDurationSec) * 100) : 0;

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <Loader2 className="h-7 w-7 animate-spin text-[#007A7A]" />
      </div>
    );
  }

  if (needsLogin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-5 w-5 text-[#D3D86F]" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Sign in to take a mock test</h1>
            <p className="text-sm text-gray-600 mb-6">Track your score estimate across full-length exams.</p>
            <Button onClick={() => goToGoogleAuth('/gmat/mock-test')} className="w-full bg-black hover:bg-gray-900 text-white rounded-full h-11">
              Sign in with Google
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col" data-testid="gmat-mock">
      <Seo title="GMAT Mock Test · Winhouse" canonical="/gmat/mock-test" />

      {/* Sticky timer bar (only during exam, only when mock is loaded) */}
      {phase === 'running' && mock?.sections?.length > 0 && (
        <div className="sticky top-0 z-30 bg-black text-white border-b border-white/10 backdrop-blur">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Badge className="bg-[#D3D86F] text-black hover:bg-[#D3D86F] flex-shrink-0">
                Section {secIdx + 1} of {mock.sections.length}
              </Badge>
              <span className="text-sm font-semibold truncate">{SECTION_LABEL[currentSection] || currentSection}</span>
              <span className="text-xs text-white/60 hidden sm:inline">·</span>
              <span className="text-xs text-white/70 hidden sm:inline">Question {qIdx + 1} / {sectionPlan.length}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:block w-28 h-1.5 bg-white/15 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${secondsLeft < 60 ? 'bg-red-500' : 'bg-[#D3D86F]'}`}
                  style={{ width: `${timePct}%` }}
                />
              </div>
              <span
                className={`flex items-center gap-1.5 font-mono font-bold text-base ${secondsLeft < 60 ? 'text-red-400' : 'text-white'}`}
                data-testid="mock-timer"
              >
                <Clock className="h-4 w-4" /> {fmtTime(secondsLeft)}
              </span>
            </div>
          </div>
        </div>
      )}

      {phase !== 'running' && <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />}

      <main className={`flex-1 max-w-3xl mx-auto px-4 sm:px-6 pb-10 w-full ${phase === 'running' ? 'pt-6' : 'pt-24 sm:pt-28'}`}>
        {/* IDLE: Pre-test brief */}
        {phase === 'idle' && (
          <>
            <div className="mb-6">
              <div className="text-xs uppercase tracking-[0.2em] text-[#007A7A] font-semibold mb-1.5">Mock Test</div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Full-Length GMAT Mock</h1>
              <p className="text-gray-600 text-sm mt-1">Three sections · Approx 135 minutes · 205–805 estimated score.</p>
            </div>

            <Card className="border-gray-200 mb-5">
              <CardContent className="p-6 sm:p-8">
                <div className="grid sm:grid-cols-3 gap-3 mb-7">
                  {SECTIONS_ORDER.map((s, i) => {
                    const required = availability?.blueprint?.[s]?.count;
                    const available = availability?.counts?.[s];
                    const short = availability && typeof available === 'number' && typeof required === 'number' && available < required;
                    const empty = availability && available === 0;
                    return (
                      <div
                        key={s}
                        className={`rounded-xl p-4 border ${empty ? 'bg-red-50 border-red-200' : short ? 'bg-amber-50 border-amber-200' : 'bg-[#FAF9F6] border-gray-100'}`}
                      >
                        <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Section {i + 1}</div>
                        <div className="font-bold text-sm">{SECTION_LABEL[s]}</div>
                        <div className="text-xs text-gray-600 mt-1">
                          {required ? `${required} questions · ${availability?.blueprint?.[s]?.duration_min || 45} min` : '—'}
                        </div>
                        {availability && (
                          <div className={`text-[11px] mt-1.5 font-medium ${empty ? 'text-red-700' : short ? 'text-amber-800' : 'text-[#007A7A]'}`}>
                            {empty
                              ? 'Not enough questions available'
                              : short
                              ? `Only ${available} available (will use what's there)`
                              : `${available} available ✓`}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Shortage banner */}
                {availability && SECTIONS_ORDER.some((s) => {
                  const a = availability.counts?.[s] ?? 0;
                  const r = availability.blueprint?.[s]?.count ?? 0;
                  return a < r;
                }) && (
                  <div className="mb-5 flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      One or more sections have fewer questions in the bank than the official blueprint requires.
                      The mock will run with whatever's available — your score estimate is still valid but may be less precise.
                    </span>
                  </div>
                )}

                <div className="space-y-2.5 text-sm text-gray-700 mb-7">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#007A7A] mt-0.5 flex-shrink-0" />
                    <span>Answers lock once submitted — no going back to previous questions.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#007A7A] mt-0.5 flex-shrink-0" />
                    <span>Sections auto-advance when the timer reaches zero.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span>Final score is a Winhouse <strong>estimate</strong> on a 205–805 scale, not the official GMAC scaled score.</span>
                  </div>
                </div>

                <Button
                  onClick={start}
                  disabled={availability && SECTIONS_ORDER.every((s) => (availability.counts?.[s] ?? 0) === 0)}
                  className="w-full bg-[#007A7A] hover:bg-[#005f5f] text-white rounded-full h-12 text-base disabled:opacity-50"
                  data-testid="mock-start"
                >
                  Begin Mock Test <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </CardContent>
            </Card>
          </>
        )}

        {/* STARTING: loading state while mockStart is in flight */}
        {phase === 'starting' && (
          <div className="flex flex-col items-center justify-center py-20" data-testid="mock-starting">
            <Loader2 className="h-8 w-8 animate-spin text-[#007A7A] mb-3" />
            <p className="text-gray-700 font-semibold">Preparing your mock test…</p>
            <p className="text-xs text-gray-500 mt-1">Building sections from the question bank.</p>
          </div>
        )}

        {/* RUNNING */}
        {phase === 'running' && question && (
          <Card key={question.id} className="border-gray-200 shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-5 flex-wrap">
                <Badge variant="outline" className="capitalize">{question.topic.replace(/_/g, ' ')}</Badge>
                <Badge variant="outline" className="capitalize">{question.difficulty}</Badge>
              </div>

              {question.passage_text && <QuestionPassage text={question.passage_text} />}
              {question.table_or_chart_data && <QuestionTableData text={question.table_or_chart_data} />}

              <QuestionStem text={question.question_text} />

              <AnswerOptionList
                question={question}
                selectedLetter={selected}
                onSelect={(k) => setSelected(k)}
                testIdPrefix="mock-opt"
              />

              <div className="flex gap-2">
                <Button
                  onClick={submitAnswer}
                  disabled={!selected}
                  className="flex-1 bg-black hover:bg-gray-900 text-white rounded-full h-12 disabled:opacity-40"
                  data-testid="mock-submit"
                >
                  Submit & Next <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
                <Button
                  variant="outline"
                  onClick={nextQuestion}
                  className="rounded-full h-12 px-5 border-gray-300"
                >
                  Skip
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Question navigator (small) */}
        {phase === 'running' && sectionPlan.length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Section progress</div>
            <div className="flex flex-wrap gap-1.5">
              {sectionPlan.map((qid, i) => {
                const answered = !!answeredMap[qid];
                const isCurrent = i === qIdx;
                return (
                  <span
                    key={qid}
                    className={`w-7 h-7 rounded-md text-[11px] font-semibold flex items-center justify-center border ${
                      isCurrent
                        ? 'bg-black text-white border-black'
                        : answered
                        ? 'bg-[#007A7A] text-white border-[#007A7A]'
                        : 'bg-white text-gray-500 border-gray-200'
                    }`}
                  >
                    {i + 1}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* FINALIZING */}
        {phase === 'finalizing' && (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-[#007A7A] mb-3" />
            <p className="text-gray-600 text-sm">Calculating your score…</p>
          </div>
        )}

        {/* DONE */}
        {phase === 'done' && results && (
          <div data-testid="mock-results" className="animate-in fade-in slide-in-from-bottom-2 duration-500">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-[#E6F4F4] text-[#007A7A] rounded-full px-3 py-1 text-xs uppercase tracking-wider font-semibold mb-3">
                <Trophy className="h-3.5 w-3.5" /> Mock complete
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Your estimated score</h1>
            </div>

            {/* Score hero */}
            <Card className="bg-black text-white border-0 mb-6 overflow-hidden relative">
              <div
                className="absolute inset-0 opacity-50"
                style={{
                  background: 'radial-gradient(50% 60% at 50% 0%, rgba(0,122,122,0.45) 0%, transparent 70%)',
                }}
              />
              <CardContent className="relative p-8 text-center">
                <div className="text-xs uppercase tracking-[0.25em] text-white/60 mb-3">Score · 205–805</div>
                <div className="text-7xl sm:text-8xl font-bold text-[#D3D86F]" data-testid="mock-final-score">
                  {results.estimated_score}
                </div>
                <div className="text-xs text-white/50 mt-3">Estimate · not the official GMAC scaled score</div>
              </CardContent>
            </Card>

            {/* Breakdown */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-4">Section breakdown</div>
                <div className="space-y-5" data-testid="mock-breakdown">
                  {Object.entries(results.breakdown).map(([sec, b]) => (
                    <div key={sec}>
                      <div className="flex items-center justify-between mb-1.5 text-sm">
                        <span className="font-semibold">{SECTION_LABEL[sec]}</span>
                        <span className="text-gray-700">{b.correct} / {b.answered} correct · <span className="font-bold">{b.percent_correct}%</span></span>
                      </div>
                      <Progress value={b.percent_correct || 0} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => { setPhase('idle'); setMock(null); setResults(null); setAnsweredMap({}); }}
                variant="outline"
                className="rounded-full h-11 px-6"
              >
                Take another mock
              </Button>
              <Button
                onClick={() => window.location.assign('/gmat/dashboard')}
                className="bg-[#007A7A] hover:bg-[#005f5f] text-white rounded-full h-11 px-6"
              >
                Back to dashboard <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </main>

      {phase !== 'running' && <Footer />}
    </div>
  );
};

export default GmatMockTestPage;
